import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CreditCard, Briefcase, AlertCircle, Shield, XCircle,
  CheckCircle, Building, Mail, Phone, Hash, Smartphone,
  Linkedin, Globe, ExternalLink, Info,
} from 'lucide-react';
import { formatDirectLine, formatPhoneForDisplay, splitWorkContact } from '../../utils/phoneUtils';
import { StatusBadge } from '../UI/StatusBadge';
import { InfoBadge } from '../UI/InfoBadge';
import PillToggle from '../UI/PillToggle';
import { useNameCard } from '../../hooks/useNameCard';

const ALLOWED_CLASSIFICATIONS = ['staff', 'executive_director', 'managing_director'];

const CONSENT_OPTIONS = [
  { id: 'off', label: 'Off' },
  { id: 'on', label: 'On' },
];

const ConsentToggle = ({ value, onChange, disabled }) => (
  <PillToggle
    value={value ? 'on' : 'off'}
    onChange={(val) => {
      if (disabled) return;
      if ((val === 'on') !== value) onChange();
    }}
    options={disabled ? CONSENT_OPTIONS.map(o => ({ ...o, disabled: true })) : CONSENT_OPTIONS}
    variant="compact"
    theme="employee"
    className="w-24 flex-shrink-0"
    fullWidth
    ariaLabel="Consent toggle"
  />
);

/**
 * Digital Name Card Consent Section for the Legal & Compliance tab.
 * Follows design-system-reference.md patterns:
 *   - Segmented Control System for ON/OFF binary consent
 *   - Responsive two-column grid (lg:grid-cols-[3fr_2fr]) on large screens
 *   - Section containers: bg-gray-50 rounded-xl p-4
 *   - Warning panels: bg-yellow-50 border-l-4 border-yellow-400
 *
 * Props:
 *   profile        - StaffUser data (from /staff/me/)
 *   hrProfile      - HRProfile confidential data (personal phone)
 *   classification - employment_classification string
 */
const NameCardConsentSection = ({ profile, hrProfile, classification }) => {
  const {
    nameCard, hasCard, isLoading,
    createNameCard, updateNameCard, deleteNameCard,
    isCreating, isUpdating, isDeleting,
  } = useNameCard();

  const [localConsent, setLocalConsent] = useState({});
  const [acknowledged, setAcknowledged] = useState(false);

  const consentState = useMemo(() => {
    if (hasCard && nameCard) {
      return {
        designation: nameCard.consent_designation,
        department: nameCard.consent_department,
        work_email: nameCard.consent_work_email,
        work_phone: nameCard.consent_work_phone,
        extension: nameCard.consent_extension,
        personal_phone: nameCard.consent_personal_phone,
        linkedin: nameCard.consent_linkedin,
      };
    }
    return {
      designation: localConsent.designation ?? false,
      department: localConsent.department ?? false,
      work_email: localConsent.work_email ?? false,
      work_phone: localConsent.work_phone ?? false,
      extension: localConsent.extension ?? false,
      personal_phone: localConsent.personal_phone ?? false,
      linkedin: localConsent.linkedin ?? false,
    };
  }, [hasCard, nameCard, localConsent]);

  const toggleConsent = useCallback((field) => {
    if (hasCard) {
      const newVal = !consentState[field];
      const payload = { [`consent_${field}`]: newVal };
      if (!newVal) {
        payload[`show_${field}`] = false;
      }
      updateNameCard(payload);
    } else {
      setLocalConsent(prev => ({ ...prev, [field]: !prev[field] }));
    }
  }, [hasCard, consentState, updateNameCard]);

  const atLeastOneField = useMemo(() => (
    Object.values(consentState).some(Boolean)
  ), [consentState]);

  const canActivate = acknowledged && atLeastOneField && !hasCard;

  const handleActivate = useCallback(() => {
    if (!canActivate) return;
    const payload = { is_active: true };
    Object.entries(consentState).forEach(([field, val]) => {
      payload[`consent_${field}`] = val;
      payload[`show_${field}`] = val;
    });
    createNameCard(payload);
    setAcknowledged(false);
  }, [canActivate, consentState, createNameCard]);

  const handleDeactivate = useCallback(() => {
    if (!hasCard) return;
    deleteNameCard();
    setLocalConsent({});
    setAcknowledged(false);
  }, [hasCard, deleteNameCard]);

  if (!ALLOWED_CLASSIFICATIONS.includes(classification)) return null;

  const personalPhone = hrProfile?.hrprofile_personal_phone_number || '';
  const isBusy = isCreating || isUpdating || isDeleting || isLoading;
  const workContact = splitWorkContact(profile);

  const businessFields = [
    { key: 'designation', label: 'Designation', value: profile?.designation, icon: Briefcase },
    { key: 'department', label: 'Department', value: profile?.department?.department_name, icon: Building },
    { key: 'work_email', label: 'Work Email', value: profile?.work_email, icon: Mail },
    { key: 'work_phone', label: 'Work Phone Number', value: workContact.mobile ? formatPhoneForDisplay(workContact.mobile) : '', icon: Phone },
    { key: 'extension', label: 'Direct line', value: formatDirectLine(workContact.did, workContact.extension), icon: Hash },
  ];

  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <CreditCard className="w-5 h-5 text-blue-800" />
        <h3 className="text-lg font-semibold text-gray-900">Digital Name Card Consent</h3>
      </div>
      <p className="text-sm text-gray-700 mb-6">
        Create a digital name card to share your professional contact information via a QR code or shareable link.
        Select which information you authorize to be publicly visible on your card.
      </p>

      {/* Two-column responsive layout: interactive fields (left) + reference panels (right) */}
      <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-6">

        {/* ─── LEFT COLUMN: Interactive consent fields ─── */}
        <div className="space-y-4">

          {/* Business Contact Information */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Briefcase className="w-4 h-4 text-blue-800" />
              <h4 className="text-base font-semibold text-gray-900">Business Contact Information</h4>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Covered under PDPA Business Contact Information Exception (Section 4(5))
            </p>

            <div className="space-y-2">
              {/* Full Name (always on) */}
              <div className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <label className="field-label mb-0">Full Name</label>
                    <span className="text-xs text-gray-400 italic">(required)</span>
                  </div>
                  <p className="field-display mt-1" title={`${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim()}>
                    <span className="truncate min-w-0">{profile?.first_name} {profile?.last_name}</span>
                  </p>
                </div>
                <ConsentToggle value={true} onChange={() => {}} disabled={true} />
              </div>

              {/* Remaining business fields */}
              {businessFields.map(({ key, label, value, icon: Icon }) => (
                <div key={key} className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-gray-400" />
                      <label className="field-label mb-0">{label}</label>
                    </div>
                    <p className="field-display mt-1" title={value || 'Not set'}>
                      <span className="truncate min-w-0">{value || <span className="text-gray-400 italic">Not set</span>}</span>
                    </p>
                  </div>
                  <ConsentToggle
                    value={consentState[key] || false}
                    onChange={() => toggleConsent(key)}
                    disabled={isBusy}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Personal Information */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-xl">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-yellow-600" />
              <h4 className="text-base font-semibold text-gray-900">Personal Information</h4>
            </div>
            <p className="text-xs text-yellow-700 mb-3">
              The following is personal data under the PDPA. Sharing is entirely optional and requires your explicit consent.
            </p>

            <div className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-yellow-200">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-gray-400" />
                  <label className="field-label mb-0">Personal Mobile Number</label>
                </div>
                {personalPhone ? (
                  <p className="field-display mt-1" title={personalPhone}>
                    <span className="truncate min-w-0">{personalPhone}</span>
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 italic mt-1">Not available in your HR profile</p>
                )}
                <p className="text-xs text-yellow-700 mt-1">
                  This is your personal phone number from your HR profile. It will be publicly accessible on your card if enabled.
                </p>
              </div>
              <ConsentToggle
                value={consentState.personal_phone || false}
                onChange={() => toggleConsent('personal_phone')}
                disabled={isBusy || !personalPhone}
              />
            </div>
          </div>

          {/* Employee-Provided Information */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Linkedin className="w-4 h-4 text-blue-800" />
              <h4 className="text-base font-semibold text-gray-900">Employee-Provided Information</h4>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              You can add these in the Name Card tab after activating your card.
            </p>

            <div className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-gray-200">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Linkedin className="w-4 h-4 text-gray-400" />
                  <label className="field-label mb-0">LinkedIn Profile URL</label>
                </div>
                <p className="text-xs text-gray-500 mt-1">You'll enter this in the Name Card settings.</p>
              </div>
              <ConsentToggle
                value={consentState.linkedin || false}
                onChange={() => toggleConsent('linkedin')}
                disabled={isBusy}
              />
            </div>
          </div>
        </div>

        {/* ─── RIGHT COLUMN: Reference / context panels ─── */}
        <div className="space-y-4">

          {/* Company Information (always shown) */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-blue-600" />
              <h4 className="text-base font-semibold text-gray-900">Company Information</h4>
              <InfoBadge color="blue" label="always shown" />
            </div>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>Winner Engineering Pte Ltd</li>
              <li>18 Woodlands Industrial Park E1, Singapore 757738</li>
              <li>+65 6481 8423</li>
              <li>www.winner-e.sg</li>
              <li>linkedin.com/company/winner-engineering-pte-ltd</li>
            </ul>
          </div>

          {/* What will NEVER be shared */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-red-500" />
              <h4 className="text-base font-semibold text-gray-900">What will NEVER be shared</h4>
            </div>
            <ul className="space-y-1">
              {[
                'NRIC/FIN, passport, or work permit numbers',
                'Personal email address',
                'Home address or date of birth',
                'Salary, financial, or medical information',
                'Employment pass type or immigration status',
                'Staff ID',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                  <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Post-activation status (right column when card exists) */}
          {hasCard && (
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <StatusBadge status="active" label="Consent Active" showIcon />
                  {nameCard?.consent_given_at && (
                    <span className="text-xs text-gray-500">
                      since {new Date(nameCard.consent_given_at).toLocaleDateString('en-SG', {
                        day: 'numeric', month: 'short', year: 'numeric'
                      })}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-1">
                <Link
                  to="/profile?tab=name-card"
                  className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:text-blue-900 font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded-full px-3 py-1.5 bg-white border border-blue-300 hover:bg-blue-50 hover:border-blue-400 shadow-sm"
                >
                  Manage your card
                  <ExternalLink className="w-4 h-4" />
                </Link>

                <button
                  onClick={handleDeactivate}
                  disabled={isBusy}
                  className="btn btn-sm btn-danger"
                >
                  <XCircle className="w-4 h-4" />
                  {isDeleting ? 'Revoking...' : 'Revoke Consent'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Acknowledgment + Activate (full-width below both columns, only pre-activation) */}
      {!hasCard && (
        <div className="mt-6">
          <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200 mb-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={isBusy}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mt-0.5 cursor-pointer"
              />
              <span className="text-sm text-gray-700">
                I understand that the fields I've selected above will be publicly accessible via a unique link and QR code.
                I can modify my selections or revoke consent at any time from this page.
              </span>
            </label>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={handleActivate}
              disabled={!canActivate || isBusy}
              className="btn btn-primary"
            >
              <CheckCircle className="w-4 h-4" />
              {isCreating ? 'Activating...' : 'Activate Name Card'}
            </button>
            {!atLeastOneField && (
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Info className="w-4 h-4" />
                Select at least one field above
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NameCardConsentSection;
